import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { isPurchasablePlan } from '../config/plans';

const DISMISS_KEY = 'logami_dismissed_payment';
const MAX_AGE_HOURS = 48;
const INCOMPLETE_STATUSES = new Set([
  'requires_payment_method',
  'requires_confirmation',
  'requires_action',
  'processing',
]);

export function useAbandonedPayment(userId) {
  const [abandonedPayment, setAbandonedPayment] = useState(null);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    const fetch = async () => {
      try {
        const q = query(
          collection(db, 'paymentIntents'),
          where('userId', '==', userId),
          orderBy('createdAt', 'desc'),
          limit(5)
        );
        const snap = await getDocs(q);
        const cutoff = Date.now() - MAX_AGE_HOURS * 60 * 60 * 1000;

        for (const doc of snap.docs) {
          const data = doc.data();
          const id = doc.id;

          if (!INCOMPLETE_STATUSES.has(data.status)) continue;
          // A retired plan (monthly) can't be resumed at its old price.
          if (!isPurchasablePlan(data.planId)) continue;
          if (localStorage.getItem(`${DISMISS_KEY}_${id}`)) continue;

          const createdMs = data.createdAt?.toDate?.().getTime() ?? 0;
          if (createdMs < cutoff) continue;

          if (!cancelled) setAbandonedPayment({ id, ...data });
          return;
        }
      } catch (_) {
        // silent fail, non-critical
      }
    };

    fetch();
    return () => { cancelled = true; };
  }, [userId]);

  const dismiss = (id) => {
    localStorage.setItem(`${DISMISS_KEY}_${id}`, '1');
    setAbandonedPayment(null);
  };

  return { abandonedPayment, dismiss };
}
